import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { logUserEvent } from '../utils/logger';

function ReviewComponent({ courseId, materialType }) {
  const { user } = useAuth();
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Show review component for all users
  if (!user) {
    return null;
  }

  const handleSubmit = async () => {
    if (!reviewText.trim()) {
      setError('Please enter your review feedback');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`/api/student/courses/${courseId}/review`, {
        course_id: courseId,
        material_type: materialType,
        review_text: reviewText.trim()
      }, {
        headers: {
          'X-User-Id': user.id
        }
      });

      setSuccess(true);
      setReviewText('');

      logUserEvent('feedback_submitted', 'Submitted course feedback', {
        course_id: courseId,
        material_type: materialType,
        review_excerpt: reviewText.substring(0, 50) + (reviewText.length > 50 ? '...' : '')
      });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to submit review:', err);
      setError(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      sx={{
        mt: 3,
        p: 3,
        backgroundColor: '#424242', // Dark gray background
        color: 'white'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <RateReviewIcon sx={{ mr: 1, color: '#90caf9' }} />
        <Typography variant="h6" sx={{ color: 'white' }}>
          Course Material Feedback
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: '#e0e0e0' }}>
        Help improve our training materials by sharing your feedback on this {materialType}.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you! Your feedback has been submitted successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={4}
        placeholder="Share your thoughts on the content quality, clarity, usefulness, or suggestions for improvement..."
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        disabled={submitting}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#616161',
            color: 'white',
            '& fieldset': {
              borderColor: '#757575',
            },
            '&:hover fieldset': {
              borderColor: '#90caf9',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#90caf9',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#bdbdbd',
            opacity: 1,
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !reviewText.trim()}
          startIcon={submitting ? <CircularProgress size={20} /> : <RateReviewIcon />}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
            '&:disabled': {
              backgroundColor: '#424242',
              color: '#757575',
            },
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </Box>
    </Paper>
  );
}

export default ReviewComponent;
